<template>
  <div class="criterion-row">
    <div class="criterion-field column-field">
      <label class="criterion-label">
        <i class="fa fa-table-columns me-2"></i>
        Column
      </label>
      <Multiselect
        v-model="localCriterion.column"
        :options="columns"
        placeholder="Select column..."
        :searchable="true"
        :disabled="isViewOnly"
        @select="handleColumnSelect"
        @clear="handleColumnClear"
      />
    </div>

    <div class="criterion-field condition-field">
      <label class="criterion-label">
        <i class="fa fa-filter me-2"></i>
        Condition
      </label>
      <Multiselect
        v-model="localCriterion.condition"
        :options="conditionOptions"
        placeholder="Select condition..."
        :searchable="false"
        :disabled="isViewOnly"
        @select="handleConditionChange"
      />
    </div>

    <div class="criterion-field values-field">
      <label class="criterion-label">
        <i class="fa fa-list me-2"></i>
        {{ valuesLabel }}
      </label>

      <!-- IN condition: Multi-select values -->
      <div v-if="localCriterion.condition === 'IN'">
        <div v-if="loadingValues" class="text-center py-2">
          <div class="spinner-border spinner-border-sm text-primary"></div>
          <span class="ms-2 text-muted small">Loading values...</span>
        </div>
        <Multiselect
          v-else
          v-model="localCriterion.values"
          placeholder="Select or type values..."
          :searchable="true"
          mode="tags"
          :options="columnValueOptions"
          :disabled="isViewOnly || !localCriterion.column"
          :no-options-text="localCriterion.column ? 'No values found' : 'Please select column first'"
          :create-option="true"
          :add-option-on="['enter', ',']"
          @change="emitUpdate"
        />
      </div>

      <!-- BETWEEN condition: Two inputs -->
      <div v-else-if="localCriterion.condition === 'BETWEEN'" class="between-fields">
        <div class="between-field">
          <small class="text-muted">From</small>
          <Multiselect
            v-model="localCriterion.lowerbound"
            placeholder="Lower bound..."
            :searchable="true"
            :options="columnValueOptions"
            :disabled="isViewOnly || !localCriterion.column"
            :create-option="true"
            @change="emitUpdate"
          />
        </div>
        <div class="between-field">
          <small class="text-muted">To</small>
          <Multiselect
            v-model="localCriterion.upperbound"
            placeholder="Upper bound..."
            :searchable="true"
            :options="columnValueOptions"
            :disabled="isViewOnly || !localCriterion.column"
            :create-option="true"
            @change="emitUpdate"
          />
        </div>
      </div>

      <!-- EQUAL condition: Single value -->
      <div v-else-if="localCriterion.condition === 'EQUAL'">
        <div v-if="loadingValues" class="text-center py-2">
          <div class="spinner-border spinner-border-sm text-primary"></div>
        </div>
        <Multiselect
          v-else
          v-model="localCriterion.singleValue"
          placeholder="Select or type value..."
          :searchable="true"
          :options="columnValueOptions"
          :disabled="isViewOnly || !localCriterion.column"
          :create-option="true"
          @change="emitUpdate"
        />
      </div>

      <!-- CONTAINS condition -->
      <div v-else>
        <input
          type="text"
          class="form-control"
          v-model="localCriterion.singleValue"
          placeholder="Enter value..."
          :disabled="isViewOnly || !localCriterion.column"
          @input="emitUpdate"
        />
      </div>
    </div>

    <div v-if="!isViewOnly" class="delete-btn-container">
      <button
        class="btn btn-sm btn-outline-danger"
        @click="$emit('delete')"
        title="Delete criterion"
      >
        <i class="fa fa-trash"></i>
      </button>
    </div>
  </div>
</template>

<script>
import Multiselect from '@vueform/multiselect'
import { sanitizeCriterionValues } from '../../utils/criterionUtils'

export default {
  name: 'Criterion',
  components: {
    Multiselect,
  },
  props: {
    criterion: {
      type: Object,
      default: () => ({
        column: null,
        condition: 'IN',
        values: [],
        lowerbound: null,
        upperbound: null,
        singleValue: null,
      })
    },
    columns: {
      type: Array,
      default: () => []
    },
    columnValues: {
      type: Object,
      default: () => ({})
    },
    loadingValues: {
      type: Boolean,
      default: false
    },
    isViewOnly: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      localCriterion: {
        column: null,
        condition: 'IN',
        values: [],
        lowerbound: null,
        upperbound: null,
        singleValue: null,
      },
      conditionOptions: [
        { value: 'IN', label: 'IN' },
        { value: 'BETWEEN', label: 'BETWEEN' },
        { value: 'EQUAL', label: 'EQUAL' },
      ],
      isUpdatingFromProp: false, // Flag to prevent update loops
    }
  },
  emits: ['update:criterion', 'columnSelected', 'delete'],
  mounted() {
    this.localCriterion = { ...this.criterion }
  },
  computed: {
    valuesLabel() {
      if (this.localCriterion.condition === 'BETWEEN') {
        return 'Range'
      } else if (this.localCriterion.condition === 'EQUAL') {
        return 'Value'
      }
      return 'Values'
    },
    columnValueOptions() {
      // Multiselect crashes on a null option (isSelected reads option.group), and
      // criteria saved before the builders were fixed can still hold one
      return sanitizeCriterionValues(this.columnValues[this.localCriterion.column])
    },
  },
  methods: {
    handleColumnSelect(value) {
      this.localCriterion.values = []
      this.localCriterion.lowerbound = null
      this.localCriterion.upperbound = null
      this.localCriterion.singleValue = null
      this.$emit('columnSelected', value)
      this.emitUpdate()
    },
    handleColumnClear() {
      this.localCriterion.column = null
      this.localCriterion.values = []
      this.localCriterion.lowerbound = null
      this.localCriterion.upperbound = null
      this.localCriterion.singleValue = null
      this.emitUpdate()
    },
    handleConditionChange() {
      // Clear values when condition changes
      this.localCriterion.values = []
      this.localCriterion.lowerbound = null
      this.localCriterion.upperbound = null
      this.localCriterion.singleValue = null
      this.emitUpdate()
    },
    emitUpdate() {
      this.$emit('update:criterion', { ...this.localCriterion })
    },
  },
  watch: {
    criterion: {
      handler(newVal) {
        // Prevent update loop - only update if values actually changed
        if (JSON.stringify(newVal) !== JSON.stringify(this.localCriterion)) {
          this.isUpdatingFromProp = true
          this.localCriterion = { ...newVal }
          this.$nextTick(() => {
            this.isUpdatingFromProp = false
          })
        }
      },
      deep: true
    },
    // Watch for changes in localCriterion values and emit updates automatically
    // This ensures updates are captured even if @change events don't fire
    'localCriterion.values': {
      handler() {
        if (!this.isUpdatingFromProp) {
          this.emitUpdate()
        }
      },
      deep: true
    },
    'localCriterion.lowerbound': {
      handler() {
        if (!this.isUpdatingFromProp) {
          this.emitUpdate()
        }
      }
    },
    'localCriterion.upperbound': {
      handler() {
        if (!this.isUpdatingFromProp) {
          this.emitUpdate()
        }
      }
    },
    'localCriterion.singleValue': {
      handler() {
        if (!this.isUpdatingFromProp) {
          this.emitUpdate()
        }
      }
    },
    'localCriterion.column': {
      handler() {
        if (!this.isUpdatingFromProp) {
          this.emitUpdate()
        }
      }
    },
    'localCriterion.condition': {
      handler() {
        if (!this.isUpdatingFromProp) {
          this.emitUpdate()
        }
      }
    },
  },
}
</script>

<style scoped>
@import '../../../node_modules/@vueform/multiselect/themes/default.css';

.criterion-row {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: white;
  padding: 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
}

.criterion-row:hover {
  border-color: var(--bs-primary, #2C7BE5);
  box-shadow: 0 2px 8px rgba(13, 110, 253, 0.1);
}

.criterion-field {
  flex: 1;
}

.criterion-field.column-field {
  flex: 1.5;
}

.criterion-field.condition-field {
  flex: 1;
}

.criterion-field.values-field {
  flex: 2;
}

.criterion-label {
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
  color: #12263f;
  margin-bottom: 0.5rem;
}

.between-fields {
  display: flex;
  gap: 0.5rem;
}

.between-field {
  flex: 1;
}

.between-field small {
  display: block;
  margin-bottom: 0.25rem;
}

.delete-btn-container {
  display: flex;
  align-items: flex-end;
  padding-bottom: 0.25rem;
}

.delete-btn-container .btn {
  padding: 0.5rem 0.75rem;
  transition: all 0.2s ease;
}

.delete-btn-container .btn:hover {
  transform: scale(1.1);
  background-color: var(--bs-danger);
  color: white;
}

/* Multiselect customizations */
:deep(.multiselect) {
  border: 1px solid #d2ddec;
  border-radius: 0.375rem;
}

:deep(.multiselect.is-disabled) {
  background-color: #edf2f9;
}

:deep(.multiselect-tag) {
  background: #dbeafe;
  color: #2563eb;
}

@media (max-width: 992px) {
  .criterion-row {
    flex-wrap: wrap;
  }

  .criterion-field {
    flex: 1 1 100%;
  }

  .delete-btn-container {
    flex: 1 1 100%;
    justify-content: center;
    margin-top: 0.5rem;
  }
}
</style>
